import React, { useState } from "react";
import styles from "./generate-statement-modal.module.scss";
import CircularProgress from "@mui/material/CircularProgress";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import dayjs, { Dayjs } from "dayjs";

type DateTarget = "from" | "to";

const GenerateStatementModal = ({
  onClose,
  onGenerate,
  isGenerating = false,
}: {
  onClose: () => void;
  onGenerate: (fromDate: string, toDate: string) => void;
  isGenerating?: boolean;
}) => {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  // The calendar is always visible inside the modal; the From/To fields just
  // choose which of the two dates it writes to. Nothing floats, so nothing
  // can be positioned off-screen.
  const [target, setTarget] = useState<DateTarget>("from");

  const fromValue: Dayjs | null = fromDate ? dayjs(fromDate) : null;
  const toValue: Dayjs | null = toDate ? dayjs(toDate) : null;
  const activeValue = target === "from" ? fromValue : toValue;

  const canGenerate = !!fromDate && !!toDate && !isGenerating;

  const handleSelect = (date: Dayjs | null) => {
    if (!date) return;

    if (target === "from") {
      setFromDate(date.toISOString());
      // An end date that now sits before the start date is no longer valid.
      if (toValue && date.isAfter(toValue, "day")) {
        setToDate("");
      }
      setTarget("to");
      return;
    }

    setToDate(date.toISOString());
  };

  const renderField = (
    fieldTarget: DateTarget,
    header: string,
    value: Dayjs | null
  ) => (
    <div className={styles.field}>
      <span className={styles.fieldHeader}>{header}</span>
      <div
        className={`${styles.fieldBox} ${
          target === fieldTarget ? styles.fieldBoxActive : ""
        }`}
        role="button"
        onClick={() => setTarget(fieldTarget)}
      >
        <span className={value ? styles.fieldValue : styles.fieldPlaceholder}>
          {value ? value.format("DD/MM/YYYY") : "DD/MM/YY"}
        </span>
      </div>
    </div>
  );

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.close} onClick={onClose}>
          ✕
        </button>

        <h2 className={styles.title}>Select Date</h2>

        <div className={styles.dateRow}>
          {renderField("from", "From", fromValue)}
          {renderField("to", "To", toValue)}
        </div>

        <div className={styles.calendarWrap}>
          <span className={styles.calendarCaption}>
            {target === "from" ? "Selecting start date" : "Selecting end date"}
          </span>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar
              value={activeValue}
              onChange={handleSelect}
              referenceDate={activeValue ?? fromValue ?? undefined}
              disableFuture
              minDate={target === "to" ? fromValue ?? undefined : undefined}
              maxDate={target === "from" ? toValue ?? undefined : undefined}
              views={["year", "month", "day"]}
              openTo="day"
            />
          </LocalizationProvider>
        </div>

        <button
          className={styles.button}
          disabled={!canGenerate}
          onClick={() => onGenerate(fromDate, toDate)}
        >
          {isGenerating ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            "Generate"
          )}
        </button>
      </div>
    </div>
  );
};

export default GenerateStatementModal;
